/**
 * VerEditalPage — visualização do Edital gerado (fase Edital).
 *
 * Réplica do view_edital.html: cabeçalho, ações, cards da cadeia
 * (DFD/ETP/TR → Edital), sumário e as 15 seções do modelo TCU.
 */

import {
  faArrowLeft,
  faCalendar,
  faCheck,
  faCheckCircle,
  faChevronRight,
  faEdit,
  faExclamationTriangle,
  faEye,
  faFileAlt,
  faFileContract,
  faFileWord,
  faGavel,
  faListOl,
  faPlus,
  faRobot,
  faScroll,
  faSpinner,
  faTrashAlt,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  documentoEdital,
  excluirEdital,
  type DocumentoEdital,
} from '../../api/edital'
import { useToast } from '../../components/Toast'

function CardCadeia({
  titulo, descricao, icone, gradiente, estado, verUrl, editarUrl,
}: {
  titulo: string
  descricao: string
  icone: IconDefinition
  gradiente: string
  estado: 'atual' | 'pronto' | 'pendente'
  verUrl?: string
  editarUrl?: string
}) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-slate-700 dark:bg-dark-card">
      <div className="mb-4 flex items-start justify-between">
        <span className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg transition-transform duration-300 group-hover:scale-110 ${gradiente}`}>
          <FontAwesomeIcon icon={icone} className="text-lg text-white" />
        </span>
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
          estado === 'atual'
            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300'
            : estado === 'pronto'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
              : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
        }`}>
          {estado !== 'pendente' && <FontAwesomeIcon icon={faCheck} className="mr-1" />}
          {estado === 'atual' ? 'Visualizando' : estado === 'pronto' ? 'Criado' : 'Pendente'}
        </span>
      </div>
      <h3 className="mb-1 text-lg font-bold text-slate-800 dark:text-white">{titulo}</h3>
      <p className="mb-5 min-h-[2.5em] text-xs text-slate-500 dark:text-slate-400">{descricao}</p>
      <div className="flex gap-2">
        {verUrl && (
          <a href={verUrl} className="flex flex-1 items-center justify-center rounded-lg border border-purple-200 bg-white py-2 text-sm font-semibold text-purple-700 shadow-sm transition-all hover:bg-purple-600 hover:text-white dark:border-purple-800 dark:bg-slate-700 dark:text-purple-400">
            <FontAwesomeIcon icon={faEye} className="mr-2" />
            Ver
          </a>
        )}
        {editarUrl && (
          <a href={editarUrl} aria-label={`Editar ${titulo}`} className="flex w-10 items-center justify-center rounded-lg border border-blue-200 bg-white py-2 text-blue-700 shadow-sm transition-all hover:bg-blue-600 hover:text-white dark:border-blue-800 dark:bg-slate-700 dark:text-blue-400">
            <FontAwesomeIcon icon={faEdit} />
          </a>
        )}
      </div>
    </div>
  )
}

function ModalExcluir({ edital, aoFechar }: { edital: DocumentoEdital; aoFechar: () => void }) {
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const excluir = useMutation({
    mutationFn: () => excluirEdital(edital.id),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['processos-licitatorios'] })
      notificar('success', 'Edital excluído. O processo e o restante da cadeia foram mantidos.')
      navegar('/processos')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog" aria-modal="true" aria-label="Excluir Edital"
      onMouseDown={(e) => { if (e.target === e.currentTarget) aoFechar() }}
    >
      <div className="mx-4 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-2xl dark:border-slate-700 dark:bg-dark-card">
        <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-600 shadow-lg shadow-red-500/30">
          <FontAwesomeIcon icon={faTrashAlt} className="text-2xl text-white" />
        </span>
        <h3 className="mb-2 text-xl font-bold text-slate-800 dark:text-slate-100">Excluir Edital?</h3>
        <p className="mb-4 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-800 dark:bg-slate-700 dark:text-slate-200">{edital.objeto}</p>
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-left dark:bg-red-900/20">
          <p className="mb-2 text-sm font-medium text-red-700 dark:text-red-400">Esta ação irá:</p>
          <ul className="list-inside list-disc space-y-1 text-sm text-red-600 dark:text-red-400">
            <li>Remover as 15 seções do Edital</li>
            <li>Manter o processo, o DFD, o ETP e o TR</li>
          </ul>
        </div>
        <p className="mb-6 text-sm font-semibold text-red-600 dark:text-red-400">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          Esta ação não pode ser desfeita!
        </p>
        <div className="flex justify-center gap-3">
          <button type="button" onClick={aoFechar} className="rounded-xl bg-slate-200 px-6 py-3 font-medium text-slate-700 transition-all hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300">
            Cancelar
          </button>
          <button type="button" onClick={() => excluir.mutate()} disabled={excluir.isPending} className="rounded-xl bg-gradient-to-r from-red-500 to-orange-600 px-6 py-3 font-semibold text-white shadow-lg shadow-red-500/30 transition-all hover:from-red-600 hover:to-orange-700 disabled:opacity-60">
            <FontAwesomeIcon icon={excluir.isPending ? faSpinner : faTrashAlt} spin={excluir.isPending} className="mr-2" />
            Sim, Excluir
          </button>
        </div>
      </div>
    </div>
  )
}

export function VerEditalPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const [excluindo, setExcluindo] = useState(false)

  const { data: edital, isLoading, error } = useQuery({
    queryKey: ['edital-documento', idNumerico],
    queryFn: () => documentoEdital(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando Edital…
      </p>
    )
  }
  if (error || !edital) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faScroll} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">Edital não encontrado</h1>
        <p className="mb-6 text-slate-500">{(error as Error)?.message ?? 'O documento não existe ou não é seu.'}</p>
        <Link to="/processos" className="inline-flex items-center rounded-lg bg-purple-600 px-6 py-3 font-semibold text-white hover:bg-purple-700">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>
    )
  }

  const secoesPreenchidas = edital.secoes.filter((s) => s.html)

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-8 text-center">
        <h1 className="mb-2 text-4xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faScroll} className="mr-3 text-purple-600" />
          Edital de Licitação
          {edital.numero_edital && ` Nº ${edital.numero_edital}`}
        </h1>
        <p className="mb-4 text-xl text-slate-600 dark:text-slate-400">{edital.objeto}</p>
        <div className="flex flex-wrap justify-center gap-4">
          {edital.criado_em && (
            <span className="inline-flex items-center rounded-full bg-purple-100 px-4 py-2 text-sm font-medium text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
              <FontAwesomeIcon icon={faCalendar} className="mr-2" />
              Criado em: {edital.criado_em}
            </span>
          )}
          <span className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-medium ${
            edital.completude.preenchidas === edital.completude.total
              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
          }`}>
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            {edital.completude.preenchidas}/{edital.completude.total} seções
          </span>
          {edital.gerado_por_ia && (
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
              <FontAwesomeIcon icon={faRobot} className="mr-2" />
              Gerado com IA
            </span>
          )}
          <span className="inline-flex items-center rounded-full bg-violet-100 px-4 py-2 text-sm font-medium text-violet-800 dark:bg-violet-900/40 dark:text-violet-300">
            <FontAwesomeIcon icon={faGavel} className="mr-2" />
            Art. 25 | Modelo TCU
          </span>
          {edital.valor_estimado && (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-4 py-2 text-sm font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
              {edital.valor_estimado}
            </span>
          )}
        </div>
      </header>

      <div className="mb-8 flex flex-col justify-center gap-4 sm:flex-row">
        <Link to={`/edital/${edital.id}/editar`} className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-blue-700">
          <FontAwesomeIcon icon={faEdit} className="mr-2" />
          Editar
        </Link>
        <a href={`/edital/${edital.id}/export/docx`} className="inline-flex items-center justify-center rounded-lg bg-purple-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-purple-700">
          <FontAwesomeIcon icon={faFileWord} className="mr-2" />
          Exportar DOCX
        </a>
        <button type="button" onClick={() => setExcluindo(true)} className="inline-flex items-center justify-center rounded-lg bg-red-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-red-700">
          <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
          Excluir
        </button>
        <Link to="/processos" className="inline-flex items-center justify-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-slate-700">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <CardCadeia titulo="DFD" descricao="Documento de Formalização da Demanda" icone={faFileAlt}
          gradiente="from-green-500 to-emerald-600"
          estado={edital.cadeia.tem_dfd ? 'pronto' : 'pendente'}
          verUrl={edital.cadeia.tem_dfd ? `/app/dfd/${edital.id}` : undefined} />
        <CardCadeia titulo="ETP" descricao="Estudo Técnico Preliminar" icone={faFileContract}
          gradiente="from-indigo-500 to-purple-600"
          estado={edital.cadeia.tem_etp ? 'pronto' : 'pendente'}
          verUrl={edital.cadeia.tem_etp ? `/app/etp/${edital.id}` : undefined} />
        <CardCadeia titulo="TR" descricao="Termo de Referência" icone={faFileContract}
          gradiente="from-emerald-500 to-teal-600"
          estado={edital.cadeia.tem_tr ? 'pronto' : 'pendente'}
          verUrl={edital.cadeia.tem_tr ? `/app/tr/${edital.id}` : undefined} />
        <CardCadeia titulo="Edital" descricao="Edital de Licitação" icone={faScroll}
          gradiente="from-purple-500 to-violet-600" estado="atual"
          editarUrl={`/app/edital/${edital.id}/editar`} />
      </div>

      <nav aria-label="Sumário" className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-white">
          <FontAwesomeIcon icon={faListOl} className="mr-2 text-purple-600" />
          Sumário
        </h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {secoesPreenchidas.map((secao) => (
            <a key={secao.chave} href={`#secao-${secao.chave}`}
              className="block rounded-lg px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-purple-50 hover:text-purple-600 dark:text-slate-400 dark:hover:bg-purple-900/20 dark:hover:text-purple-400">
              {secao.titulo}
            </a>
          ))}
        </div>
      </nav>

      <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-purple-600 to-violet-600 p-6">
          <h2 className="flex items-center text-2xl font-bold text-white">
            <FontAwesomeIcon icon={faScroll} className="mr-3" />
            Edital de Licitação
          </h2>
          <p className="mt-1 text-purple-100">
            Lei 14.133/2021 - Art. 25 | Modelo TCU
          </p>
        </div>

        <div className="space-y-6 p-8">
          {edital.secoes.map((secao, indice) => (
            secao.html ? (
              <section key={secao.chave} id={`secao-${secao.chave}`}
                className="overflow-hidden rounded-lg border border-slate-200 transition-shadow hover:shadow-md dark:border-slate-700">
                <h3 className="flex items-center border-b border-slate-200 bg-slate-50 px-6 py-4 text-lg font-semibold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                  <FontAwesomeIcon icon={faChevronRight} className="mr-2 text-sm text-purple-500" />
                  {secao.chave === 'preambulo' ? secao.titulo : `${indice}. ${secao.titulo}`}
                </h3>
                <div className="p-6">
                  <div className="conteudo-documento leading-relaxed text-slate-700 dark:text-slate-300"
                    dangerouslySetInnerHTML={{ __html: secao.html }} />
                </div>
              </section>
            ) : null
          ))}
          {secoesPreenchidas.length === 0 && (
            <div className="py-8 text-center text-slate-500">
              <FontAwesomeIcon icon={faEdit} className="mb-3 text-3xl" />
              <p>Nenhuma seção preenchida ainda.</p>
              <Link to={`/edital/${edital.id}/editar`} className="mt-3 inline-flex items-center text-purple-600 hover:text-purple-800">
                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                Preencher agora
              </Link>
            </div>
          )}
        </div>
      </article>

      {excluindo && <ModalExcluir edital={edital} aoFechar={() => setExcluindo(false)} />}
    </div>
  )
}
